/**
 * Application Configuration
 *
 * Environment-specific configuration and constants.
 *
 * Runtime values injected via `window.__ENV__` (populated by docker-entrypoint.sh)
 * take precedence over build-time `import.meta.env` values so the same Docker
 * image can be deployed to any environment.
 */

import { API_ROUTES } from './api-routes';

/** Runtime env injected by docker-entrypoint.sh → env-config.js */
const runtimeEnv: Record<string, string | undefined> =
  typeof window !== 'undefined'
    ? ((window as unknown as Record<string, unknown>).__ENV__ as Record<string, string | undefined> ?? {})
    : {};

/** Return the first non-empty string, or fallback. */
function env(key: string, fallback = ''): string {
  return runtimeEnv[key] || import.meta.env[key] || fallback;
}

export const config = {
  app: {
    name: 'TMS Product Management Portal',
    version: env('VITE_APP_VERSION', '1.0.0'),
    environment: import.meta.env.MODE,
  },
  api: {
    // Empty default = same-origin requests through the Vite dev proxy (vite.config.mts),
    // required so the PHX-1180 auth cookie (SameSite=Strict) is sent on local dev —
    // set VITE_API_URL explicitly to call the gateway directly instead.
    baseUrl: env('VITE_API_URL', ''),
    timeout: 30000,
  },
  /** Hospital context — populated at runtime by setAuthContext() after login. */
  hospitalId: '',
  /** Tenant context — populated at runtime by setAuthContext() after login. */
  tenantId: '',
  /** Staff ID — populated at runtime by setAuthContext() after login. */
  staffId: '',
  /** UUID of the user creating a record. Populated after login. */
  createdBy: '',
  logging: {
    level: import.meta.env.DEV ? 'debug' : 'info',
    console: true,
    beaconUrl: import.meta.env.PROD ? `${API_ROUTES.SERVICE}/logs` : undefined,
    beaconLevel: 'warn' as const,
    serviceName: 'product-portal',
  },
  telemetry: {
    enabled: !!env('NEW_RELIC_BROWSER_APP_ID'),
    serviceName: 'product-portal',
    environment: import.meta.env.MODE,
    beaconUrl: `${API_ROUTES.SERVICE}/telemetry`,
    applicationId: env('NEW_RELIC_BROWSER_APP_ID'),
    licenseKey: env('NEW_RELIC_BROWSER_LICENSE_KEY'),
  },
  features: {
    enableDevTools: import.meta.env.DEV,
    enableMockData: env('VITE_ENABLE_MOCK') === 'true',
  },
} as const;

export type AppConfig = typeof config;

/**
 * Update auth-derived config values after login or on page-reload hydration.
 */
export function setAuthContext(ctx: {
  staffId?: string;
  hospitalId?: string;
  tenantId?: string;
}): void {
  if (ctx.hospitalId) {
    (config as Record<string, unknown>).hospitalId = ctx.hospitalId;
  }
  if (ctx.tenantId) {
    (config as Record<string, unknown>).tenantId = ctx.tenantId;
  }
  if (ctx.staffId) {
    (config as Record<string, unknown>).staffId = ctx.staffId;
    (config as Record<string, unknown>).createdBy = ctx.staffId;
  }
}

/**
 * Reset auth-derived config values on logout.
 */
export function clearAuthContext(): void {
  (config as Record<string, unknown>).staffId = '';
  (config as Record<string, unknown>).hospitalId = '';
  (config as Record<string, unknown>).tenantId = '';
  (config as Record<string, unknown>).createdBy = '';
}
